import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState, InputObservable } from '@y42/angular/util';
import { combineLatest, map, Observable, of, switchMap, timer } from 'rxjs';
import { DateTimeFormat } from '../date-time.model';
import { DateTimeService } from '../date-time.service';

const ONE_MINUTE = 60_000;

interface RelativeConfig {
  showIfDaysDiff: number;
  format: DateTimeFormat;
}

const DEFAULT_RELATIVE_CONFIG: RelativeConfig = {
  showIfDaysDiff: 1,
  format: DateTimeFormat.ShortDateTime,
};

@UntilDestroy()
@Component({
  selector: 'y42-time-ago',
  templateUrl: './time-ago.component.html',
  styleUrls: ['./time-ago.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeAgoComponent {
  constructor(private readonly dateTimeService: DateTimeService) {}

  @Input()
  @InputObservable()
  date: Date | undefined;
  date$!: Observable<Date | undefined>;

  @Input()
  @InputObservable()
  relativeConfig: Partial<RelativeConfig> | undefined;
  relativeConfig$!: Observable<Partial<RelativeConfig> | undefined>;

  @Input() showTooltip = false;

  public readonly state = connectState(this, {
    timeAgo: combineLatest([
      this.date$,
      this.relativeConfig$.pipe(
        map((relativeConfig) => ({
          ...DEFAULT_RELATIVE_CONFIG,
          ...relativeConfig,
        })),
      ),
    ]).pipe(
      switchMap(([date, { showIfDaysDiff, format }]) =>
        date ? this.formatTimeAgo(date, showIfDaysDiff, format) : of(''),
      ),
    ),
  });

  private formatTimeAgo(timeAgo: Date, showRelativeIfDaysDiff: number, nonRelativeFormat: DateTimeFormat) {
    const showRelative = this.dateTimeService.diff(timeAgo, new Date(), 'days') < showRelativeIfDaysDiff;
    if (showRelative) {
      return timer(0, ONE_MINUTE).pipe(
        map(() => this.dateTimeService.formatDateRelativeToNow(timeAgo)),
        untilDestroyed(this),
      );
    }
    return of(this.dateTimeService.formatDate(timeAgo, nonRelativeFormat));
  }
}
